/**
 * 蒙版绘制弹窗（用于 inpaint 局部重绘）
 *
 * 功能：
 *  - 在参考图上绘制蒙版（标记需要重绘的区域）
 *  - 笔画级撤销/重做：每笔记录 { 模式, 笔刷大小, 点集 }，undo/redo 全量重放
 *  - 画笔 / 橡皮 / 清空重画
 *  - Alt + 横向拖拽 调节笔刷大小（带光标预览圈）
 *  - 视口缩放/平移：滚轮缩放、抓手工具或中键拖拽平移、一键复位
 *  - 导出黑白蒙版图（白色=重绘区域，黑色=保留区域）；初始蒙版作为底层与笔迹合并
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  Eraser,
  Hand,
  Loader2,
  Maximize,
  Paintbrush,
  Redo2,
  Trash2,
  Undo2,
  Wand2,
  X,
} from 'lucide-react';
import { loadImage } from '../utils/imageTransform';
import { resolveBrowserMediaUrl } from '../utils/storageUrl';

interface MaskDialogProps {
  imageUrl: string;
  initialMask?: string | null;
  showPromptInput?: boolean;
  initialPrompt?: string;
  submitLabel?: string;
  submitting?: boolean;
  onSave: (payload: { maskDataUrl: string; prompt: string }) => void;
  onClose: () => void;
}

const CANVAS_SIZE = 512;
const BRUSH_MIN = 5;
const BRUSH_MAX = 160;
/** 视口缩放范围 */
const VIEW_MIN = 0.4;
const VIEW_MAX = 4;

interface StrokePoint {
  x: number;
  y: number;
}
interface MaskStroke {
  mode: 'paint' | 'erase';
  size: number;
  points: StrokePoint[];
}

export function MaskDialog({
  imageUrl,
  initialMask,
  showPromptInput = false,
  initialPrompt = '',
  submitLabel = '提交重绘',
  submitting = false,
  onSave,
  onClose,
}: MaskDialogProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // 初始蒙版底层（单独一 canvas，笔迹撤销不会影响已保存的蒙版）
  const baseCanvasRef = useRef<HTMLCanvasElement>(null);

  const [brushSize, setBrushSize] = useState(30);
  const [isEraser, setIsEraser] = useState(false);
  const [isPanTool, setIsPanTool] = useState(false);
  const [imgSize, setImgSize] = useState<{ width: number; height: number } | null>(null);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [hasInk, setHasInk] = useState(false);

  // 笔画历史（撤销 = 弹出末笔入重做栈并重放；重做反之）
  const strokesRef = useRef<MaskStroke[]>([]);
  const redoRef = useRef<MaskStroke[]>([]);
  const [historyTick, setHistoryTick] = useState(0); // 触发按钮可用态刷新
  const drawingRef = useRef(false);
  const currentStrokeRef = useRef<MaskStroke | null>(null);

  // 视口（缩放 + 平移）
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const panRef = useRef<{ startX: number; startY: number; tx: number; ty: number } | null>(null);
  const spaceRef = useRef(false);

  // Alt 横向拖拽调笔刷
  const sizeAdjustRef = useRef<{ startX: number; startSize: number } | null>(null);
  const [sizeAdjusting, setSizeAdjusting] = useState(false);

  // 光标预览圈位置（画布坐标系）
  const cursorRef = useRef<HTMLDivElement>(null);

  // ---------------- 挂 body 标记：弹窗打开期间屏蔽画布全局快捷键 ----------------
  useEffect(() => {
    document.body.dataset.boardModalOpen = 'true';
    return () => {
      delete document.body.dataset.boardModalOpen;
    };
  }, []);

  // ---------------- 加载底图与初始蒙版 ----------------
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const img = await loadImage(resolveBrowserMediaUrl(imageUrl));
        if (cancelled) return;
        setImgSize({ width: img.naturalWidth, height: img.naturalHeight });
      } catch {
        /* 图片加载失败，仍可绘制 */
      }
      // 初始蒙版画入底层
      if (initialMask) {
        try {
          const maskImg = await loadImage(initialMask.startsWith('blob:') || initialMask.startsWith('data:')
            ? initialMask
            : resolveBrowserMediaUrl(initialMask));
          if (cancelled) return;
          const base = baseCanvasRef.current;
          if (base) {
            const ctx = base.getContext('2d');
            ctx?.drawImage(maskImg, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
            setHasInk(true);
          }
        } catch {
          /* 蒙版加载失败，从空白开始 */
        }
      } else {
        setHasInk(false);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [imageUrl, initialMask]);

  // ---------------- 绘制 ----------------
  const getCanvasPos = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_SIZE / rect.width;
    const scaleY = CANVAS_SIZE / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  /** 在 ctx 上画一条完整笔画（replay 与实时增量共用） */
  const paintStroke = (ctx: CanvasRenderingContext2D, stroke: MaskStroke) => {
    ctx.lineWidth = stroke.size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (stroke.mode === 'erase') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = 'rgba(249,115,22,0.45)';
    }
    ctx.beginPath();
    const pts = stroke.points;
    if (pts.length === 1) {
      ctx.arc(pts[0].x, pts[0].y, stroke.size / 2, 0, Math.PI * 2);
      ctx.fillStyle = stroke.mode === 'erase' ? 'rgba(0,0,0,1)' : 'rgba(249,115,22,0.45)';
      ctx.fill();
    } else {
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = 'source-over';
  };

  /** 全量重放（undo / redo 后调用） */
  const replayAll = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    for (const stroke of strokesRef.current) paintStroke(ctx, stroke);
  }, []);

  const drawSegment = (from: StrokePoint, to: StrokePoint) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const size = currentStrokeRef.current?.size ?? brushSize;
    const mode = currentStrokeRef.current?.mode ?? (isEraser ? 'erase' : 'paint');
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (mode === 'erase') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = 'rgba(249,115,22,0.45)';
    }
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Alt + 横向拖拽 = 调笔刷大小，不落笔
    if (e.altKey) {
      sizeAdjustRef.current = { startX: e.clientX, startSize: brushSize };
      setSizeAdjusting(true);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      return;
    }
    if (isPanTool || e.button === 1 || spaceRef.current) {
      panRef.current = { startX: e.clientX, startY: e.clientY, tx: view.tx, ty: view.ty };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    e.preventDefault();
    drawingRef.current = true;
    const pos = getCanvasPos(e);
    const stroke: MaskStroke = { mode: isEraser ? 'erase' : 'paint', size: brushSize, points: [pos] };
    currentStrokeRef.current = stroke;
    // 画一个点
    drawSegment(pos, pos);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // 更新光标预览圈
    if (cursorRef.current) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        cursorRef.current.style.left = `${e.clientX - rect.left}px`;
        cursorRef.current.style.top = `${e.clientY - rect.top}px`;
        cursorRef.current.style.width = `${brushSize}px`;
        cursorRef.current.style.height = `${brushSize}px`;
      }
    }

    // Alt 调大小：横向位移映射笔刷尺寸
    if (sizeAdjustRef.current) {
      const dx = e.clientX - sizeAdjustRef.current.startX;
      const next = Math.round(
        Math.min(BRUSH_MAX, Math.max(BRUSH_MIN, sizeAdjustRef.current.startSize + dx * 0.5))
      );
      setBrushSize(next);
      return;
    }

    // 视口平移
    if (panRef.current) {
      setView((v) => ({
        ...v,
        tx: panRef.current!.tx + (e.clientX - panRef.current!.startX),
        ty: panRef.current!.ty + (e.clientY - panRef.current!.startY),
      }));
      return;
    }

    if (!drawingRef.current || !currentStrokeRef.current) return;
    const pos = getCanvasPos(e);
    const pts = currentStrokeRef.current.points;
    const last = pts[pts.length - 1];
    drawSegment(last, pos);
    pts.push(pos);
  };

  const endInteraction = () => {
    if (sizeAdjustRef.current) {
      sizeAdjustRef.current = null;
      setSizeAdjusting(false);
    }
    panRef.current = null;
    if (drawingRef.current && currentStrokeRef.current) {
      strokesRef.current.push(currentStrokeRef.current);
      redoRef.current = [];
      currentStrokeRef.current = null;
      setHasInk(true);
      setHistoryTick((t) => t + 1);
    }
    drawingRef.current = false;
  };

  const onPointerLeave = () => {
    if (cursorRef.current) {
      cursorRef.current.style.left = '-9999px';
    }
    endInteraction();
  };

  // ---------------- 撤销 / 重做 ----------------
  const undo = useCallback(() => {
    const popped = strokesRef.current.pop();
    if (!popped) return;
    redoRef.current.push(popped);
    replayAll();
    setHistoryTick((t) => t + 1);
  }, [replayAll]);

  const redo = useCallback(() => {
    const popped = redoRef.current.pop();
    if (!popped) return;
    strokesRef.current.push(popped);
    replayAll();
    setHistoryTick((t) => t + 1);
  }, [replayAll]);

  // 弹窗内快捷键（body 标记已屏蔽画布全局快捷键）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((mod && e.key.toLowerCase() === 'z' && e.shiftKey) || (mod && e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        redo();
      } else if (e.code === 'Space') {
        spaceRef.current = true;
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceRef.current = false;
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [undo, redo]);

  // ---------------- 视口滚轮缩放（以光标为中心） ----------------
  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    setView((v) => {
      const nextScale = Math.min(VIEW_MAX, Math.max(VIEW_MIN, v.scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
      const ratio = nextScale / v.scale;
      // 保持光标下的画布点不动
      return {
        scale: nextScale,
        tx: cx - (cx - v.tx) * ratio,
        ty: cy - (cy - v.ty) * ratio,
      };
    });
  };

  const resetView = () => setView({ scale: 1, tx: 0, ty: 0 });

  const clearCanvas = () => {
    strokesRef.current = [];
    redoRef.current = [];
    const base = baseCanvasRef.current;
    base?.getContext('2d')?.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    replayAll();
    setHistoryTick((t) => t + 1);
  };

  // ---------------- 导出：底层蒙版(亮度) 与 笔迹(alpha) 合并为黑白图 ----------------
  const handleSave = () => {
    const paint = canvasRef.current;
    const base = baseCanvasRef.current;
    if (!paint) return;

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = CANVAS_SIZE;
    exportCanvas.height = CANVAS_SIZE;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return;

    // 黑底（保留区域）
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const out = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    const outPixels = out.data;

    // 底层初始蒙版：亮度 > 128 视为重绘区
    const baseCtx = base?.getContext('2d');
    const basePixels = baseCtx?.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;
    // 笔迹层：alpha > 10 视为重绘区
    const paintCtx = paint.getContext('2d');
    const paintPixels = paintCtx?.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;

    for (let i = 0; i < outPixels.length; i += 4) {
      let mask = false;
      if (basePixels) {
        const lum = 0.299 * basePixels[i] + 0.587 * basePixels[i + 1] + 0.114 * basePixels[i + 2];
        if (basePixels[i + 3] > 10 && lum > 128) mask = true;
      }
      if (!mask && paintPixels && paintPixels[i + 3] > 10) mask = true;
      if (mask) {
        outPixels[i] = 255;
        outPixels[i + 1] = 255;
        outPixels[i + 2] = 255;
        outPixels[i + 3] = 255;
      }
    }

    ctx.putImageData(out, 0, 0);
    onSave({ maskDataUrl: exportCanvas.toDataURL('image/png'), prompt: prompt.trim() });
  };

  const canUndo = strokesRef.current.length > 0;
  const canRedo = redoRef.current.length > 0;
  void historyTick; // 仅用于触发重渲染读取 ref 长度

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex w-[880px] max-w-[94vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Paintbrush className="h-4 w-4 text-orange-500" />
            <h3 className="text-sm font-semibold text-slate-900">绘制蒙版</h3>
            <span className="text-xs text-slate-400">涂抹区域 = 重绘区域 · Alt+拖拽 调笔刷 · 滚轮缩放</span>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex">
          {/* 画布区（带视口缩放平移） */}
          <div
            className="relative flex flex-1 items-center justify-center overflow-hidden bg-slate-900 p-4"
            style={{ height: 560 }}
            onWheel={onWheel}
          >
            <div
              className="relative"
              style={{
                width: CANVAS_SIZE,
                height: CANVAS_SIZE,
                transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
                transformOrigin: 'center center',
              }}
            >
              {/* 底图 */}
              <img
                src={resolveBrowserMediaUrl(imageUrl)}
                alt="底图"
                className="absolute inset-0 h-full w-full object-contain"
                draggable={false}
              />
              {/* 蒙版绘制层 */}
              <canvas
                ref={canvasRef}
                width={CANVAS_SIZE}
                height={CANVAS_SIZE}
                className={`absolute inset-0 h-full w-full ${
                  isPanTool ? 'cursor-grab' : 'cursor-crosshair'
                }`}
                style={{ touchAction: 'none' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endInteraction}
                onPointerLeave={onPointerLeave}
              />
              {/* 底层初始蒙版（隐藏，仅导出时合成） */}
              <canvas ref={baseCanvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} className="hidden" />
              {/* 光标预览圈 */}
              <div
                ref={cursorRef}
                className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-orange-400/90 bg-orange-400/20"
                style={{ left: -9999, top: -9999 }}
              />
            </div>

            {/* 视口控制（右下角） */}
            <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-lg bg-slate-950/60 p-1 backdrop-blur-sm">
              <button
                type="button"
                onClick={() => setView((v) => ({ ...v, scale: Math.max(VIEW_MIN, v.scale / 1.2) }))}
                className="grid h-7 w-7 place-items-center rounded-md text-slate-300 transition hover:bg-white/10 hover:text-white"
                title="缩小"
              >
                −
              </button>
              <span className="w-10 text-center text-[11px] tabular-nums text-slate-300">{Math.round(view.scale * 100)}%</span>
              <button
                type="button"
                onClick={() => setView((v) => ({ ...v, scale: Math.min(VIEW_MAX, v.scale * 1.2) }))}
                className="grid h-7 w-7 place-items-center rounded-md text-slate-300 transition hover:bg-white/10 hover:text-white"
                title="放大"
              >
                +
              </button>
              <button
                type="button"
                onClick={resetView}
                className="grid h-7 w-7 place-items-center rounded-md text-slate-300 transition hover:bg-white/10 hover:text-white"
                title="复位视图"
              >
                <Maximize className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* 右侧控制面板 */}
          <div className="w-[280px] space-y-4 border-l border-slate-200 p-4">
            {showPromptInput && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Wand2 className="h-4 w-4 text-orange-500" />
                  <label htmlFor="mask-dialog-prompt" className="text-sm font-medium text-slate-700">
                    局部重绘提示词
                  </label>
                </div>
                <textarea
                  id="mask-dialog-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="例如：把中央图标改成发光能量核心，保留整体背景和版式不变"
                  className="min-h-28 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />
                <div className="mt-2 text-[11px] leading-5 text-slate-400">
                  提示词和蒙版会一起提交，蒙版外区域会尽量保持不变。
                </div>
              </div>
            )}

            {/* 笔刷大小 */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-slate-600">画笔大小</label>
                <span className="text-xs tabular-nums text-slate-400">{brushSize}px</span>
              </div>
              <input
                type="range"
                min={BRUSH_MIN}
                max={BRUSH_MAX}
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-full accent-orange-500"
              />
              <div className="mt-1 text-[10px] text-slate-400">提示：按住 Alt 横向拖拽可快速调节</div>
            </div>

            {/* 工具切换 */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsEraser(false);
                  setIsPanTool(false);
                }}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition ${
                  !isEraser && !isPanTool ? 'bg-orange-500 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Paintbrush className="h-3.5 w-3.5" />
                画笔
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEraser(true);
                  setIsPanTool(false);
                }}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition ${
                  isEraser && !isPanTool ? 'bg-orange-500 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Eraser className="h-3.5 w-3.5" />
                橡皮
              </button>
              <button
                type="button"
                onClick={() => setIsPanTool((v) => !v)}
                className={`flex items-center justify-center rounded-lg px-2.5 py-2 text-xs font-medium transition ${
                  isPanTool ? 'bg-slate-700 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                title="抓手工具（或按住空格/中键拖拽）"
              >
                <Hand className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* 撤销 / 重做 / 清空 */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={undo}
                disabled={!canUndo}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                title="撤销一笔 (Ctrl+Z)"
              >
                <Undo2 className="h-3.5 w-3.5" />
                撤销
              </button>
              <button
                type="button"
                onClick={redo}
                disabled={!canRedo}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                title="重做 (Ctrl+Shift+Z)"
              >
                <Redo2 className="h-3.5 w-3.5" />
                重做
              </button>
              <button
                type="button"
                onClick={clearCanvas}
                disabled={!hasInk || submitting}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" />
                清空
              </button>
            </div>

            {imgSize && (
              <div className="text-[10px] text-slate-400">
                原图尺寸: {imgSize.width} × {imgSize.height}
              </div>
            )}
          </div>
        </div>

        {/* 底部操作 */}
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <span className="text-xs text-slate-400">
            用画笔涂抹需要重新生成的区域
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={sizeAdjusting || (showPromptInput && !prompt.trim()) || submitting}
              className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {submitting ? '提交中' : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
